import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

import { selectSessionAdvisor, fetchSessionForAuthRequest } from '@client/model';
import { advisorsApi } from '@client/api/waterlily';

export const advisorBasicInfoSchema = z.object({
  advisorFirstName: z.string().min(1, 'First name is required'),
  advisorLastName: z.string().min(1, 'Last name is required'),
  organizationDisplayName: z.string().min(1, 'Organization name is required'),
  organizationName: z.string().optional(),
});

export type AdvisorBasicInfoFormValues = z.infer<typeof advisorBasicInfoSchema>;

export function useAdvisorOnboardingBasicInfoForm() {
  const dispatch = useDispatch();

  const advisor = useSelector(selectSessionAdvisor);

  const form = useForm<AdvisorBasicInfoFormValues>({
    resolver: zodResolver(advisorBasicInfoSchema),
    mode: 'onChange',
    defaultValues: {
      advisorFirstName: '',
      advisorLastName: '',
      organizationDisplayName: '',
      organizationName: '',
    },
  });

  const updateAdvisorMutation = useMutation({
    mutationFn: (data: AdvisorProfile) => advisorsApi.updateById(advisor.advisorId!, data),
    onSuccess: () => {
      toast.success('Profile Saved', {
        description: 'Your information has been saved. Click Next to continue.',
      });
      dispatch(fetchSessionForAuthRequest({ invalidating: true }));
    },
    onError: () => {
      toast.error('Failed to save profile', {
        description: 'An unexpected error occurred. Please try again.',
      });
    },
  });

  // Check if the advisor profile has all required fields filled (from the persisted advisor data)
  const isProfileComplete = useMemo(() => {
    return Boolean(
      advisor.advisorFirstName?.trim() &&
        advisor.advisorLastName?.trim() &&
        advisor.organizationDisplayName?.trim(),
    );
  }, [advisor.advisorFirstName, advisor.advisorLastName, advisor.organizationDisplayName]);

  useEffect(() => {
    if (advisor.advisorId && !form.formState.isDirty) {
      form.reset({
        advisorFirstName: advisor.advisorFirstName ?? '',
        advisorLastName: advisor.advisorLastName ?? '',
        organizationDisplayName: advisor.organizationDisplayName ?? '',
        organizationName: advisor.organizationName ?? '',
      });
    }
  }, [
    advisor.advisorId,
    advisor.advisorFirstName,
    advisor.advisorLastName,
    advisor.organizationDisplayName,
    advisor.organizationName,
    form,
  ]);

  const onSubmit = (data: AdvisorBasicInfoFormValues) => {
    if (!advisor.advisorId) {
      toast.error('Unable to proceed', {
        description: 'Advisor ID is missing. Please refresh and try again.',
      });
      return;
    }

    const displayName = data.organizationDisplayName.trim();
    const legalName = data.organizationName?.trim() || displayName;

    updateAdvisorMutation.mutate(
      {
        ...advisor,
        advisorFirstName: data.advisorFirstName.trim(),
        advisorLastName: data.advisorLastName.trim(),
        organizationName: legalName,
        organizationDisplayName: displayName,
      },
      {
        onSuccess: () => {
          // Reset the form with the saved values to clear the dirty state
          form.reset({
            advisorFirstName: data.advisorFirstName.trim(),
            advisorLastName: data.advisorLastName.trim(),
            organizationDisplayName: displayName,
            organizationName: legalName,
          });
        },
      },
    );
  };

  return {
    form,
    onSubmit: form.handleSubmit(onSubmit),
    isSubmitting: updateAdvisorMutation.isPending,
    isValid: form.formState.isValid,
    isProfileComplete,
  };
}
